import React, { useEffect, useState } from 'react';
import axios from '../../utils/axiosInstance';
import { Link } from 'react-router-dom';
import { FaEye } from 'react-icons/fa';
import { toastSuccess, toastError } from '../../utils/alertHelper';

const GuestIndex = () => {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showColumnFilters, setShowColumnFilters] = useState(false);

  const getInitialLimit = () => {
    const stored = localStorage.getItem('guest_page_limit');
    return stored ? parseInt(stored) : 10;
  };
  const [limit, setLimit] = useState(getInitialLimit());

  // Only guest-relevant fields based on the guest creation form
  const allColumns = [
    { key: 'firstName', label: 'First Name' },
    { key: 'middleName', label: 'Middle Name' },
    { key: 'mobileNumber', label: 'Mobile Number' },
    { key: 'city', label: 'City' },
    { key: 'businessName', label: 'Company Name' },
    { key: 'email', label: 'Email' },
  ];

  const getInitialVisibleColumns = () => {
    const stored = localStorage.getItem('guest_visible_columns');
    return stored ? JSON.parse(stored) : ['firstName', 'middleName', 'mobileNumber', 'city', 'businessName', 'email'];
  };
  const [visibleColumns, setVisibleColumns] = useState(getInitialVisibleColumns);

  useEffect(() => {
    localStorage.setItem('guest_page_limit', limit);
  }, [limit]);

  useEffect(() => {
    localStorage.setItem('guest_visible_columns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    fetchUsers(currentPage, searchQuery);
  }, [currentPage, searchQuery, limit]);

  const fetchUsers = async (page = 1, search = '') => {
    try {
      const params = new URLSearchParams({
        userType: 'Guest',
        page,
        limit,
        search,
      });

      const res = await axios.get(`/api/user?${params.toString()}`);
      const { users = [], totalCount = 0 } = res.data;
      setUsers(users);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      toastError('Failed to fetch users');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/user/${id}`);
      toastSuccess('User deleted successfully!');
      setCurrentPage(1);
      fetchUsers(1, searchQuery);
    } catch (err) {
      toastError('Failed to delete user');
    }
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0 text-dark"> Guests</h3>
        <div className="d-flex align-items-center gap-2">
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ width: 250 }}
            placeholder="Search Guest..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
          <button
            className="btn btn-sm d-flex align-items-center gap-1"
            style={{
              backgroundColor: showColumnFilters ? '#C44C23' : 'transparent',
              color: showColumnFilters ? '#fff' : '#C44C23',
              border: '1px solid #F36B24',
            }}
            onClick={() => setShowColumnFilters(!showColumnFilters)}
          >
            <FaEye />
          </button>
        </div>
      </div>

      {showColumnFilters && (
        <div className="mb-2 text-end position-relative" style={{ minWidth: 220 }}>
          <div
            className="dropdown-menu show p-3"
            style={{
              maxHeight: 250,
              overflowY: 'auto',
              right: 0,
              left: 'auto',
              position: 'absolute',
              zIndex: 1050,
              width: 'max-content',
              minWidth: 220,
            }}
          >
            {allColumns.map(col => (
              <div key={col.key} className="form-check mb-1">
                <input
                  type="checkbox"
                  id={`col-filter-${col.key}`}
                  className="form-check-input"
                  checked={visibleColumns.includes(col.key)}
                  onChange={e => {
                    if (e.target.checked) {
                      setVisibleColumns(prev => [...prev, col.key]);
                    } else {
                      setVisibleColumns(prev => prev.filter(k => k !== col.key));
                    }
                  }}
                />
                <label htmlFor={`col-filter-${col.key}`} className="form-check-label" style={{ fontSize: '13px' }}>
                  {col.label}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0 text-dark">All Guests</h5>
          <Link to="/guest/create" className="btn btn-sm" style={{ backgroundColor: '#C44C23', color: '#fff' }}>
            + Add New Guest
          </Link>
        </div>

        <div className="card-body p-0">
          <table className="table table-bordered table-sm mb-0" style={{ fontSize: '14px', tableLayout: 'auto', width: '100%' }}>
            <thead className="table-light">
              <tr>
                <th className="py-1">Sr no.</th>
                {visibleColumns.map(key => {
                  const col = allColumns.find(c => c.key === key);
                  return <th key={key} className="py-1">{col?.label}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {users.length > 0 ? (
                users.map((u, idx) => (
                  <tr key={u._id}>
                    <td>{(currentPage - 1) * limit + idx + 1}</td>
                    {visibleColumns.map(key => {
                      let value = '';
                      if (key === 'firstName') {
                        value = u.firstName;
                        return (
                          <td key={key}>
                            <Link
                              to={`/guest/view/${u._id}`}
                              style={{ color: '#C44C23', textDecoration: 'underline', cursor: 'pointer' }}
                            >
                              {value || '-'}
                            </Link>
                          </td>
                        );
                      }
                      else if (key === 'middleName') value = u.middleName;
                      else if (key === 'mobileNumber') value = u.mobileNumber;
                      else if (key === 'email') value = u.email;
                      else if (key === 'city') value = u.city?.name;
                      else if (key === 'businessName') {
                        value = u.business && u.business.length > 0 ? u.business[0].businessName : '';
                      }
                      return <td key={key}>{value || '-'}</td>;
                    })}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="text-center">No guests found.</td>
                </tr>
              )}
            </tbody>
          </table>

           {/* Control Row */}
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 pb-2 pt-2">
            <div>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto' }}
                value={limit}
                onChange={e => {
                  setLimit(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {[10, 25, 50, 100].map(val => (
                  <option key={val} value={val}>{val}</option>
                ))}
              </select>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo;
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className="btn btn-sm"
                  style={{
                    backgroundColor: currentPage === page ? '#C44C23' : 'transparent',
                    color: currentPage === page ? 'white' : '#C44C23',
                    border: '1px solid #F36B24'
                  }}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestIndex;
